import React, { useEffect, useState } from "react";
import Wrapper from "../../../components/Wrapper";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Paper,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  GetLandlordInterestRequests,
  UpdateInterestRequestStatus,
  ConfirmMoveIn,
} from "../../../redux/reducer/FlatInterestRequest";
import { toast, ToastContainer } from "react-toastify";
import { GetAccount } from "../../../redux/reducer/GetAccountDetails";
import moment from "moment";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupIcon from "@mui/icons-material/Group";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RefreshIcon from "@mui/icons-material/Refresh";
import title from "../../../components/title";
import BankDetailsModal from "../../../components/BankDetailsModal";

// The interest form stores DD-MM-YYYY. The confirm button stays disabled until
// the move-in date has actually arrived — an owner cannot certify that someone
// moved in on a day that has not happened yet.
const isMoveInDue = (moveInDate) => {
  if (!moveInDate) return false;

  const match = String(moveInDate).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  const date = match
    ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
    : new Date(moveInDate);

  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date <= today;
};

export default function InterestRequests() {
  title("Interest Requests");

  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { requests, loading } = useSelector((state) => state.flatInterest || {});

  const [activeTab, setActiveTab] = useState(0);
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, action: "" });
  const [responseMessage, setResponseMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  // Bank details modal state
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [pendingAcceptRequest, setPendingAcceptRequest] = useState(null);
  const [checkingBankDetails, setCheckingBankDetails] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const landlordId = localStorage.getItem("user_id") || localStorage.getItem("userId");

  useEffect(() => {
    if (landlordId) {
      dispatch(GetLandlordInterestRequests({ landlordId }));
    }
  }, [dispatch, landlordId]);

  const handleRefresh = () => {
    if (landlordId) {
      dispatch(GetLandlordInterestRequests({ landlordId }));
    }
  };

  const handleConfirmMoveIn = async (request) => {
    const requestId = request._id || request.id;
    setConfirmingId(requestId);

    const res = await dispatch(ConfirmMoveIn({ requestId, landlordId }));
    setConfirmingId(null);

    if (res?.payload?.success) {
      toast.success(res.payload.message || "Move-in confirmed");
      handleRefresh();
    } else {
      toast.error(res?.payload?.message || "Could not confirm move-in");
    }
  };

  // Check bank details before accepting
  const handleAcceptClick = async (request) => {
    setCheckingBankDetails(true);
    try {
      const res = await dispatch(GetAccount({ userId: landlordId }));
      const bankData = res?.payload?.data?.data;

      if (bankData && bankData.bankName && bankData.accountNumber && bankData.accountName) {
        // Bank details exist - proceed with accept dialog
        handleOpenActionDialog(request, "accepted");
      } else {
        // No bank details - show bank modal first
        setPendingAcceptRequest(request);
        setBankModalOpen(true);
      }
    } catch (err) {
      // On error, show bank modal to be safe
      setPendingAcceptRequest(request);
      setBankModalOpen(true);
    } finally {
      setCheckingBankDetails(false);
    }
  };

  // Handle bank details modal success
  const handleBankDetailsSuccess = () => {
    setBankModalOpen(false);
    if (pendingAcceptRequest) {
      // After bank details saved, open the accept confirmation dialog
      handleOpenActionDialog(pendingAcceptRequest, "accepted");
      setPendingAcceptRequest(null);
    }
  };

  const handleOpenActionDialog = (request, action) => {
    setActionDialog({ open: true, request, action });
    setResponseMessage("");
    setActionError("");
  };

  const handleCloseActionDialog = () => {
    setActionDialog({ open: false, request: null, action: "" });
    setResponseMessage("");
    setActionError("");
  };

  const handleConfirmAction = async () => {
    const { request, action } = actionDialog;
    if (!request) return;

    setActionLoading(true);
    setActionError("");

    try {
      const result = await dispatch(
        UpdateInterestRequestStatus({
          requestId: request._id || request.id,
          status: action,
          message: responseMessage,
        })
      );

      if (result?.payload?.data?.success || result?.payload?.status === 200) {
        handleCloseActionDialog();
        // Refresh the list
        dispatch(GetLandlordInterestRequests({ landlordId }));
      } else {
        setActionError(result?.payload?.data?.message || "Failed to update status");
      }
    } catch (err) {
      setActionError("Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // Filter requests by status
  const pendingRequests = (requests || []).filter((r) => r.status === "pending");

  // Verifying the payment flips the request to "completed", which matched none
  // of these tabs — so the moment a tenant paid, their booking vanished from
  // this page entirely and the owner had nowhere to confirm the move-in from.
  // A paid booking is still an accepted one; it belongs here.
  const acceptedRequests = (requests || []).filter(
    (r) => r.status === "accepted" || r.status === "completed"
  );
  const rejectedRequests = (requests || []).filter((r) => r.status === "rejected");

  // Bookings waiting on the owner. Ones where the tenant has already said they
  // are in come first — those are the ones actually blocking someone.
  const moveInRequests = (requests || [])
    .filter((r) => r.moveInStatus === "awaiting")
    .sort(
      (a, b) =>
        Boolean(b.tenantMarkedMoveInAt) - Boolean(a.tenantMarkedMoveInAt)
    );

  const needsConfirmation = moveInRequests.filter(
    (r) => r.tenantMarkedMoveInAt
  ).length;

  const getFilteredRequests = () => {
    switch (activeTab) {
      case 0:
        return pendingRequests;
      case 1:
        return acceptedRequests;
      case 2:
        return moveInRequests;
      case 3:
        return rejectedRequests;
      default:
        return requests || [];
    }
  };

  const renderStatusChip = (status) => {
    if (status === "pending") {
      return <Chip icon={<HourglassEmptyIcon />} label="Pending" color="warning" size="small" />;
    } else if (status === "accepted") {
      return <Chip icon={<CheckCircleIcon />} label="Accepted" color="success" size="small" />;
    } else if (status === "rejected") {
      return <Chip icon={<CancelIcon />} label="Rejected" color="error" size="small" />;
    }
    return null;
  };

  const renderRequestCard = (request) => (
    <Grid item xs={12} md={6} lg={4} key={request._id || request.id}>
      <Card elevation={2} sx={{ height: "100%" }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {request.tenantName || "Unknown Tenant"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {moment(request.createdAt || request.created_at).format("DD MMM YYYY, hh:mm A")}
                </Typography>
              </Box>
            </Box>
            {renderStatusChip(request.status)}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Flat Info */}
          <Paper elevation={0} sx={{ bgcolor: "#f5f5f5", p: 1.5, borderRadius: 1, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HomeIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                {request.flatName || "Flat"}
              </Typography>
            </Box>
          </Paper>

          {/* Tenant Details */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">{request.tenantEmail || "N/A"}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">{request.tenantPhone || "N/A"}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Move-in: {request.moveInDate || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Family Size: {request.familySize || "N/A"}
              </Typography>
            </Box>
            {request.occupation && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WorkIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                  {request.occupation}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Message */}
          {request.message && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Message:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: "italic", bgcolor: "#fafafa", p: 1, borderRadius: 1 }}>
                "{request.message}"
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          {request.status === "pending" && (
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={checkingBankDetails ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={() => handleAcceptClick(request)}
                disabled={checkingBankDetails}
                fullWidth
              >
                {checkingBankDetails ? "Checking..." : "Accept"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => handleOpenActionDialog(request, "rejected")}
                disabled={checkingBankDetails}
                fullWidth
              >
                Reject
              </Button>
            </Box>
          )}

          {/* Move-in confirmation.

              Verifying the payment books the flat; it does not start the
              tenancy. Nothing unlocks for the tenant — bills, maintenance,
              rent payment — until the owner says here that the tenant is
              actually in. One button, and only from the move-in date. */}
          {request.moveInStatus === "awaiting" &&
            (() => {
              const tenantSaysSo = Boolean(request.tenantMarkedMoveInAt);
              // The tenant reporting it is stronger evidence than the calendar,
              // so it opens the button even if they moved in early.
              // A cash booking cannot become a tenancy until OTS has verified
              // its own service fee. The server refuses too; this says why.
              const feeBlocking =
                Boolean(request.otsFee?.required) &&
                request.otsFee.status !== "verified";
              const canConfirm =
                !feeBlocking && (tenantSaysSo || isMoveInDue(request.moveInDate));
              const busy = confirmingId === (request._id || request.id);

              return (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.8,
                    borderRadius: 2,
                    border: `1px solid ${tenantSaysSo ? "#fcd34d" : "#bfdbfe"}`,
                    bgcolor: tenantSaysSo ? "#fffbeb" : "#eff6ff",
                  }}
                >
                  {tenantSaysSo && (
                    <Chip
                      size="small"
                      label="Needs your confirmation"
                      sx={{
                        mb: 1,
                        fontWeight: 700,
                        fontSize: 11,
                        color: "#92400e",
                        bgcolor: "#fef3c7",
                      }}
                    />
                  )}

                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Has {request.tenantName?.split(" ")[0] || "the tenant"} moved
                    in?
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.4 }}
                  >
                    {tenantSaysSo
                      ? `${request.tenantName} says they have moved in. Confirm it to start the tenancy — or contact them first if that is not right.`
                      : isMoveInDue(request.moveInDate)
                      ? "Move-in date has arrived. Confirming starts the tenancy and unlocks bills, maintenance and rent for them."
                      : `Move-in is set for ${request.moveInDate}. You can confirm from that date, or sooner if the tenant tells us they have moved in.`}
                  </Typography>

                  {feeBlocking && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 1.4, color: "#92400e", fontWeight: 600 }}
                    >
                      Waiting for OTS to verify the tenant's service fee. You can
                      confirm the move-in once it is verified.
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    size="small"
                    color={tenantSaysSo ? "warning" : "primary"}
                    startIcon={
                      busy ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <HomeIcon />
                      )
                    }
                    disabled={!canConfirm || busy}
                    onClick={() => handleConfirmMoveIn(request)}
                    fullWidth
                  >
                    {busy ? "Confirming…" : "Yes, confirm move-in"}
                  </Button>
                </Box>
              );
            })()}

          {request.moveInStatus === "moved_in" && (
            <Box
              sx={{
                mt: 2,
                p: 1.4,
                borderRadius: 2,
                bgcolor: "#e8f5e9",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircleIcon fontSize="small" sx={{ color: "#2e7d32" }} />
              <Typography variant="body2">
                Move-in confirmed
                {request.moveInConfirmedBy === "auto"
                  ? " automatically after the grace period"
                  : ""}
                . Tenancy is active.
              </Typography>
            </Box>
          )}

          {request.moveInStatus === "cancelled" && (
            <Box sx={{ mt: 2, p: 1.4, borderRadius: 2, bgcolor: "#fff7ed" }}>
              <Typography variant="body2">
                {request.cancellation?.cancelledBy === "owner"
                  ? "This booking was cancelled."
                  : "Tenant cancelled this booking."}
                {request.cancellation?.reason
                  ? ` Reason: ${request.cancellation.reason}.`
                  : ""}{" "}
                {request.cancellation?.isCash ? (
                  <>
                    The payment was made to you in cash, so OTS never held it
                    and is not responsible for it — settle any refund directly
                    with the tenant.
                  </>
                ) : (
                  <>
                    Your share of the deduction:{" "}
                    <strong>
                      Rs{" "}
                      {Number(
                        request.cancellation?.landlordShare || 0
                      ).toLocaleString()}
                    </strong>
                    .
                  </>
                )}{" "}
                The property is available again.
              </Typography>
            </Box>
          )}

          {/* Response Message if any */}
          {request.responseMessage && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: request.status === "accepted" ? "#e8f5e9" : "#ffebee", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Your Response:
              </Typography>
              <Typography variant="body2">"{request.responseMessage}"</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <Wrapper />

      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h4" fontWeight={600}>
              Interest Requests
            </Typography>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ mb: 3 }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Pending
                  <Chip label={pendingRequests.length} size="small" color="warning" />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Accepted
                  <Chip label={acceptedRequests.length} size="small" color="success" />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Move-ins
                  <Chip
                    label={moveInRequests.length}
                    size="small"
                    color={needsConfirmation > 0 ? "warning" : "default"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Rejected
                  <Chip label={rejectedRequests.length} size="small" color="error" />
                </Box>
              }
            />
          </Tabs>

          {/* The tab label alone is easy to walk past, and a tenant sitting in
              a flat without bills or maintenance is waiting on this one click. */}
          {needsConfirmation > 0 && activeTab !== 2 && (
            <Alert
              severity="warning"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setActiveTab(2)}
                  sx={{ fontWeight: 700 }}
                >
                  Review
                </Button>
              }
            >
              {needsConfirmation === 1
                ? "A tenant says they have moved in and is waiting for you to confirm it."
                : `${needsConfirmation} tenants say they have moved in and are waiting for you to confirm it.`}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Empty State */}
          {!loading && getFilteredRequests().length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {activeTab === 2
                  ? "No move-ins waiting on you"
                  : `No ${
                      activeTab === 0
                        ? "pending"
                        : activeTab === 1
                        ? "accepted"
                        : "rejected"
                    } requests`}
              </Typography>
            </Box>
          )}

          {/* Requests Grid */}
          {!loading && getFilteredRequests().length > 0 && (
            <Grid container spacing={3}>
              {getFilteredRequests().map(renderRequestCard)}
            </Grid>
          )}
        </Box>

        {/* Action Dialog */}
        <Dialog open={actionDialog.open} onClose={handleCloseActionDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {actionDialog.action === "accepted" ? "Accept Request" : "Reject Request"}
          </DialogTitle>
          <DialogContent>
            {actionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {actionError}
              </Alert>
            )}

            <Typography variant="body1" sx={{ mb: 2 }}>
              {actionDialog.action === "accepted"
                ? `Are you sure you want to accept the request from ${actionDialog.request?.tenantName}?`
                : `Are you sure you want to reject the request from ${actionDialog.request?.tenantName}?`}
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Response Message (Optional)"
              placeholder={
                actionDialog.action === "accepted"
                  ? "e.g., Please contact me at this number to schedule a visit..."
                  : "e.g., Sorry, the flat has already been rented..."
              }
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseActionDialog} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={actionDialog.action === "accepted" ? "success" : "error"}
              onClick={handleConfirmAction}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bank Details Modal - shown when landlord doesn't have bank details */}
        <BankDetailsModal
          open={bankModalOpen}
          onClose={() => {
            setBankModalOpen(false);
            setPendingAcceptRequest(null);
          }}
          onSuccess={handleBankDetailsSuccess}
          tenantName={pendingAcceptRequest?.tenantName || "Tenant"}
          flatName={pendingAcceptRequest?.flatName || "Property"}
          advanceAmount={pendingAcceptRequest?.advanceAmount}
        />

        {/* This page called toast() with no container mounted, so confirming a
            move-in succeeded or failed in complete silence. */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="colored"
        />
      </div>
    </>
  );
}
